'use client';

import { c as _c } from "react/compiler-runtime";
import { AnalyticsBrowser, type AnalyticsBrowserSettings, type InitOptions } from '@segment/analytics-next';
import { createContext, type PropsWithChildren, useEffect, useState } from 'react';
import { useConstant } from '../../lib/internal/hooks';
import { BrowserStorageWrapper } from '../../lib/internal/storage';
import { type SegmentAbstraction, SegmentLive, SegmentMock } from '../../lib/Segment';
import type { BaseShoppingEvent } from '../../Shibuya';

/**
 * Analytics Context Value
 * Contains analytics instance and configuration passed through IoC
 */
export interface AnalyticsContextValue {
  analytics: SegmentAbstraction;
  deployableName: string;
  deployableVersion: string;
  deviceId?: string;
  gitSha: string;
  defaultSkuViewedSource: NonNullable<BaseShoppingEvent['sku_source']>;
  defaultSkuViewedProductType: string;
  defaultSkuViewedLevel: NonNullable<BaseShoppingEvent['sku_level']>;
}
export const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

/**
 * Analytics Provider Props
 * All configuration is injected via props (Inversion of Control)
 */
export interface AnalyticsProviderProps {
  /** Segment write key for your application */
  writeKey: string;
  /** Name of the deployable (e.g., 'cardfinder-v2') */
  deployableName: string;
  /** Version of the deployable (e.g., environment name) */
  deployableVersion: string;
  /** Git SHA of the current deployment */
  gitSha: string;
  /** Custom Segment CDN URL (default: 'https://cdn.segment.com') */
  segmentCdnUrl?: string;
  /** Custom Segment API host (default: 'api.segment.io/v1') */
  segmentApiHost?: string;
  /** Default source for SKU viewed events (default: 'marketplace') */
  defaultSkuViewedSource?: NonNullable<BaseShoppingEvent['sku_source']>;
  /** Default product type for SKU viewed events (default: 'credit_cards') */
  defaultSkuViewedProductType?: string;
  /** Default level for SKU viewed events (default: 'offer') */
  defaultSkuViewedLevel?: NonNullable<BaseShoppingEvent['sku_level']>;
  /** Enable debug mode (uses SegmentMock instead of live tracking) */
  debug?: boolean;
}
export const AnalyticsProvider = t0 => {
  const $ = _c(20);
  const {
    deployableName,
    deployableVersion,
    gitSha,
    writeKey,
    segmentCdnUrl: t1,
    segmentApiHost: t2,
    defaultSkuViewedSource: t3,
    defaultSkuViewedProductType: t4,
    defaultSkuViewedLevel: t5,
    debug: t6,
    children
  } = t0;
  const segmentCdnUrl = t1 === undefined ? "https://cdn.segment.com" : t1;
  const segmentApiHost = t2 === undefined ? "api.segment.io/v1" : t2;
  const defaultSkuViewedSource = t3 === undefined ? "marketplace" : t3;
  const defaultSkuViewedProductType = t4 === undefined ? "credit_cards" : t4;
  const defaultSkuViewedLevel = t5 === undefined ? "offer" : t5;
  const debug = t6 === undefined ? false : t6;
  const [deviceId, setDeviceId] = useState();
  let t7;
  let t8;
  if ($[0] !== debug || $[1] !== segmentApiHost || $[2] !== segmentCdnUrl || $[3] !== writeKey) {
    t7 = () => {
      if (debug) {
        return new SegmentMock();
      }
      const settings = {
        writeKey,
        cdnURL: segmentCdnUrl
      };
      const options = {
        integrations: {
          "Segment.io": {
            apiHost: segmentApiHost,
            protocol: "https",
            deliveryStrategy: {
              strategy: "standard",
              config: {
                keepalive: true
              }
            }
          }
        },
        storage: {
          stores: ["cookie", "localStorage", "memory"]
        }
      };
      const browserAnalytics = AnalyticsBrowser.load(settings, options);
      return new SegmentLive(browserAnalytics);
    };
    t8 = [writeKey, segmentCdnUrl, segmentApiHost, debug];
    $[0] = debug;
    $[1] = segmentApiHost;
    $[2] = segmentCdnUrl;
    $[3] = writeKey;
    $[4] = t7;
    $[5] = t8;
  } else {
    t7 = $[4];
    t8 = $[5];
  }
  const analytics = useConstant(t7, t8);
  let t10;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const storage = new BrowserStorageWrapper();
      const cookies = new Map(document?.cookie.split("; ").map(_temp));
      if (cookies.has("cookie_id")) {
        setDeviceId(cookies.get("cookie_id"));
      } else {
        let storedCookieId = storage.getItem("cookie_id");
        if (!storedCookieId) {
          storedCookieId = crypto.randomUUID();
          storage.setItem("cookie_id", storedCookieId);
        }
        setDeviceId(storedCookieId);
      }
    };
    t10 = [];
    $[6] = t10;
    $[7] = t9;
  } else {
    t10 = $[6];
    t9 = $[7];
  }
  useEffect(t9, t10);
  let t11;
  if ($[8] !== analytics || $[9] !== defaultSkuViewedLevel || $[10] !== defaultSkuViewedProductType || $[11] !== defaultSkuViewedSource || $[12] !== deployableName || $[13] !== deployableVersion || $[14] !== deviceId || $[15] !== gitSha) {
    t11 = {
      analytics,
      deployableName,
      deployableVersion,
      deviceId,
      gitSha,
      defaultSkuViewedSource,
      defaultSkuViewedProductType,
      defaultSkuViewedLevel
    };
    $[8] = analytics;
    $[9] = defaultSkuViewedLevel;
    $[10] = defaultSkuViewedProductType;
    $[11] = defaultSkuViewedSource;
    $[12] = deployableName;
    $[13] = deployableVersion;
    $[14] = deviceId;
    $[15] = gitSha;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== children || $[18] !== t11) {
    t12 = <AnalyticsContext.Provider value={t11}>{children}</AnalyticsContext.Provider>;
    $[17] = children;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
};
function _temp(cookieStr) {
  return cookieStr.split("=") as [string, string];
}